import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Twitter, Facebook, Linkedin, Link2, Check } from "lucide-react";
import { format } from "date-fns";
import Markdown, { headingId } from "@/components/store/Markdown";
import JsonLd from "@/components/store/JsonLd";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const fmt = (d) => { try { return format(new Date(d), "MMMM d, yyyy"); } catch { return ""; } };

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    document.title = post.meta_title || `${post.title} — Sew Voyage`;
    setMeta("description", post.meta_description || post.excerpt);
    setMeta("keywords", post.keywords);
    setMeta("og:title", post.meta_title || post.title, "property");
    setMeta("og:description", post.meta_description || post.excerpt, "property");
    setMeta("og:image", `${window.location.origin}/api/og/post/${post.slug}.jpg`, "property");
    setMeta("og:type", "article", "property");
    setMeta("twitter:card", "summary_large_image");
    return () => { document.title = "Sew Voyage"; };
  }, [post]);

  if (post === false) return <div className="py-32 text-center font-heading text-3xl">Story not found. <Link to="/journal" className="underline">Back to Journal</Link></div>;
  if (!post) return <div className="py-32 text-center font-body text-ink/40">Loading…</div>;

  const url = `${window.location.origin}/journal/${post.slug}`;
  const enc = encodeURIComponent;
  const toc = (post.content.match(/^##\s+.+$/gm) || []).map((h) => h.replace(/^##\s+/, ""));

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: [mediaUrl(post.og_image || post.cover_image)].filter(Boolean),
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: post.author || "Sew Voyage" },
    publisher: {
      "@type": "Organization",
      name: "Sew Voyage",
      logo: { "@type": "ImageObject", url: `${window.location.origin}/logo512.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    keywords: post.keywords || (post.tags || []).join(", "),
    articleSection: post.tags?.[0],
  };

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter/subscribe", { email, name: "" }); toast.success("You're on the list."); setEmail(""); }
    catch { toast.error("Could not subscribe."); }
  };
  const shareLink = `${window.location.origin}/api/share/post/${post.slug}`;
  const copyLink = () => {
    api.post("/share/track", { kind: "post", slug: post.slug }).catch(() => {});
    navigator.clipboard.writeText(shareLink).then(() => { setCopied(true); toast.success("Copied! Share link is on your clipboard."); setTimeout(() => setCopied(false), 2000); }).catch(() => window.prompt("Copy this share link:", shareLink));
  };

  return (
    <article className="pb-24" data-testid="journal-post-page">
      <JsonLd id="ld-article" data={articleLd} />
      <div className="max-w-3xl mx-auto px-6 pt-14 text-center">
        <Link to="/journal" className="inline-flex items-center gap-2 font-body uppercase tracking-wide2 text-[0.6rem] text-ink/50 hover:text-ink transition-colors mb-8" data-testid="journal-back"><ArrowLeft size={13} /> The Journal</Link>
        <p className="eyebrow mb-4">{post.tags?.[0]}</p>
        <h1 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6" data-testid="post-title">{post.title}</h1>
        <p className="font-body text-ink/60 text-[0.7rem] uppercase tracking-wide2">{post.author} · {fmt(post.published_at)} · {post.read_minutes} min read</p>
      </div>

      <div className="max-w-5xl mx-auto px-6 my-12">
        <div className="overflow-hidden bg-cream aspect-[16/9]"><img src={mediaUrl(post.cover_image)} alt={post.cover_alt || post.title} className="w-full h-full object-cover vintage-photo" data-testid="post-cover" /></div>
      </div>

      <div className="max-w-3xl mx-auto px-6 grid lg:grid-cols-[1fr] gap-10 relative">
        {toc.length > 1 && (
          <aside className="hidden xl:block absolute -left-56 top-0 w-48" data-testid="post-toc">
            <p className="eyebrow mb-3">In this story</p>
            <ul className="space-y-2">
              {toc.map((h) => <li key={h}><a href={`#${headingId(h)}`} className="font-body text-xs text-ink/50 hover:text-ink transition-colors block leading-snug">{h}</a></li>)}
            </ul>
          </aside>
        )}
        <div>
          <Markdown content={post.content} />

          <div className="flex items-center gap-3 mt-12 pt-8 border-t border-ink/10" data-testid="post-share">
            <span className="font-body uppercase tracking-wide2 text-[0.6rem] text-ink/40 mr-1">Share</span>
            <a href={`https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(post.title)}`} target="_blank" rel="noreferrer" className="share-btn" data-testid="share-twitter" aria-label="Share on X"><Twitter size={15} /></a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noreferrer" className="share-btn" data-testid="share-facebook" aria-label="Share on Facebook"><Facebook size={15} /></a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} target="_blank" rel="noreferrer" className="share-btn" data-testid="share-linkedin" aria-label="Share on LinkedIn"><Linkedin size={15} /></a>
            <a href={`https://pinterest.com/pin/create/button/?url=${enc(url)}&media=${enc(mediaUrl(post.cover_image))}&description=${enc(post.title)}`} target="_blank" rel="noreferrer" className="share-btn" data-testid="share-pinterest" aria-label="Share on Pinterest"><span className="font-heading text-sm leading-none">P</span></a>
            <button onClick={copyLink} className="share-btn" data-testid="share-copy" aria-label="Copy link">{copied ? <Check size={15} /> : <Link2 size={15} />}</button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 mt-16">
        <div className="bg-ink text-linen p-10 text-center" data-testid="post-newsletter">
          <p className="font-body uppercase tracking-luxe text-[0.6rem] text-linen/50 mb-3">The Voyage Letter</p>
          <h3 className="font-heading text-2xl md:text-3xl mb-5">Never miss a story or a drop.</h3>
          <form onSubmit={subscribe} className="flex gap-2 max-w-sm mx-auto">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" data-testid="post-newsletter-email" className="flex-1 bg-transparent border border-linen/30 px-4 py-2.5 text-sm outline-none font-body placeholder:text-linen/40 focus:border-linen transition-colors" />
            <button className="bg-linen text-ink font-body uppercase tracking-wide2 text-[0.6rem] px-5 hover:opacity-80 transition-opacity" data-testid="post-newsletter-submit">Join</button>
          </form>
        </div>
      </div>

      {post.related?.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 mt-20">
          <p className="eyebrow text-center mb-10">Keep Reading</p>
          <div className="grid md:grid-cols-3 gap-x-8 gap-y-10">
            {post.related.map((p) => (
              <Link key={p.slug} to={`/journal/${p.slug}`} className="group block" data-testid={`related-${p.slug}`}>
                <div className="overflow-hidden bg-cream aspect-[4/3] mb-4"><img src={mediaUrl(p.cover_image)} alt={p.cover_alt || p.title} loading="lazy" className="w-full h-full object-cover vintage-photo transition-transform duration-700 group-hover:scale-105" /></div>
                <p className="eyebrow mb-1">{p.tags?.[0]}</p>
                <h4 className="font-heading text-lg tracking-tight group-hover:italic transition-all">{p.title}</h4>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
